import { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Checkbox,
  CheckboxGroup,
  Text,
  Badge,
  IconButton,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
} from '@chakra-ui/react';
import { Key, ChartLine } from 'phosphor-react';
import { useActionToast } from '../../services/actionToast';
import { PageTitle } from '../../components/PageTitle';
import { DataSkeleton, EmptyState, ErrorState } from '../../components/states';
import {
  deriveAnyLabel,
  FilterBar,
  FramedCard,
  FramedCardContent,
  getFacetIcon,
  PaginationFooter,
  RowIdentity,
} from '../../components/ui';
import { isRequestFailure, useFetch } from '../../hooks/useFetch';
import { api } from '../../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  group: {
    id: number;
    name: string;
  };
  status: boolean;
}

interface Group {
  id: number;
  name: string;
}

interface Pagination {
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

interface UserAnalytics {
  last_login: string | null;
  last_activity: string | null;
  login_count: number;
  session_duration: number | null;
  total_sessions: number;
}

const UserAccessManagementPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSecretary, setSelectedSecretary] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [permissionMode, setPermissionMode] = useState<'reuse' | 'create'>('reuse');
  const [selectedHierarchy, setSelectedHierarchy] = useState('');
  const [newHierarchyName, setNewHierarchyName] = useState('');
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [hierarchies, setHierarchies] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isAnalyticsOpen,
    onOpen: onAnalyticsOpen,
    onClose: onAnalyticsClose,
  } = useDisclosure();
  const toast = useActionToast();

  const {
    requestApi: fetchUsers,
    data: usersData,
    loading: loadingUsers,
    error: usersError,
  } = useFetch('/v1/admin/users');

  const { requestApi: fetchGroups, data: groupsData } = useFetch('/v1/admin/groups');

  const { requestApi: fetchSecretaries, data: secretariesData } = useFetch('/v1/admin/secretaries');

  const { requestApi: fetchInstitutions, data: institutionsData } =
    useFetch('/v1/admin/institutions');

  const { requestApi: fetchHierarchies } = useFetch('/v1/admin/permissions/resources');

  const { requestApi: saveUserPermissions, loading: savingPermissions } = useFetch(
    '/v1/admin/permissions/users/manage-bulk',
    { actionFeedback: false }
  );

  const loadUsers = async () => {
    const params: any = { page: currentPage, per_page: perPage };
    if (searchTerm) params.search = searchTerm;
    if (selectedGroup) params.group_id = parseInt(selectedGroup, 10);
    if (selectedSecretary) params.secretary_id = parseInt(selectedSecretary, 10);
    if (selectedInstitution) params.institution_id = parseInt(selectedInstitution, 10);

    await fetchUsers('get', params);
  };

  const loadGroups = async () => {
    await fetchGroups('get', { page: 1, per_page: 100 });
  };

  const loadSecretaries = async () => {
    await fetchSecretaries('get', { page: 1, per_page: 100 });
  };

  const loadInstitutions = async () => {
    const params: any = { page: 1, per_page: 100 };
    if (selectedSecretary) params.secretary_id = selectedSecretary;
    await fetchInstitutions('get', params);
  };

  const loadHierarchies = async () => {
    try {
      const response = await fetchHierarchies('get');

      // O backend retorna no formato: { data: [...], pagination: {...} }
      if (response?.data) {
        const resources = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setHierarchies(resources);
      } else {
        setHierarchies([]);
      }
    } catch (error) {
      setHierarchies([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadGroups();
    loadSecretaries();
    loadInstitutions();
    loadHierarchies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedGroup, selectedSecretary, selectedInstitution, currentPage, perPage]);

  useEffect(() => {
    if (selectedSecretary) {
      setSelectedInstitution('');
      loadInstitutions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSecretary]);

  const handleEditPermissions = (user: User) => {
    setSelectedUser(user);
    setPermissionMode('reuse');
    setSelectedHierarchy('');
    setNewHierarchyName('');
    setSelectedActions([]);
    onOpen();
  };

  const handleViewAnalytics = async (user: User) => {
    setSelectedUser(user);
    setLoadingAnalytics(true);
    onAnalyticsOpen();

    try {
      const response = await api.get(`/v1/admin/users/${user.id}`);
      const userData = response.data?.data;

      setUserAnalytics({
        last_login: userData?.last_login || null,
        last_activity: userData?.last_activity || null,
        login_count: userData?.login_count || 0,
        session_duration: userData?.session_duration || null,
        total_sessions: userData?.login_count || 0,
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados de analytics do usuário',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onAnalyticsClose();
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    try {
      return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Data inválida';
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds || seconds === 0) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    // Validações
    if (permissionMode === 'reuse' && !selectedHierarchy) {
      toast({
        title: 'Erro',
        description: 'Selecione uma hierarquia existente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (permissionMode === 'create') {
      if (!newHierarchyName.trim()) {
        toast({
          title: 'Erro',
          description: 'Informe o nome da nova hierarquia',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (hierarchies.includes(newHierarchyName.trim())) {
        toast({
          title: 'Erro',
          description: 'Já existe uma hierarquia com este nome',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (selectedActions.length === 0) {
        toast({
          title: 'Erro',
          description: 'Selecione pelo menos uma ação',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      const payload = {
        user_id: selectedUser.id,
        mode: permissionMode,
        hierarchy: permissionMode === 'reuse' ? selectedHierarchy : newHierarchyName,
        actions: permissionMode === 'create' ? selectedActions : [],
      };

      const result = await saveUserPermissions('post', payload);
      if (isRequestFailure(result)) throw result.error;

      toast({
        title: 'Sucesso',
        description: 'Permissões atualizadas com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
      loadUsers();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao salvar permissões',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const users = usersData?.data || [];
  const paginationData = usersData?.pagination;
  const pagination: Pagination = {
    total: paginationData?.total_items || 0,
    page: paginationData?.current_page || 1,
    per_page: perPage,
    pages: paginationData?.total_pages || 1,
  };

  const handlePerPageChange = (value: string) => {
    setPerPage(parseInt(value, 10));
    setCurrentPage(1); // Reset to first page when changing perPage
  };

  return (
    <Box>
      <PageTitle description="Gerencie permissões e acessos dos usuários do sistema.">
        Gerenciar acessos
      </PageTitle>

      <FilterBar
        facets={[
          {
            key: 'group',
            label: 'Grupo',
            icon: getFacetIcon('group_id'),
            anyLabel: deriveAnyLabel('Grupo'),
            options: (Array.isArray(groupsData?.data) ? groupsData?.data ?? [] : []).map(
              (group: Group) => ({ value: String(group.id), label: group.name })
            ),
            values: selectedGroup ? [String(selectedGroup)] : [],
            apply: (values: string[]) => setSelectedGroup(values[0] ?? ''),
          },
          {
            key: 'secretary',
            label: 'Secretaria',
            icon: getFacetIcon('secretary_id'),
            anyLabel: deriveAnyLabel('Secretaria'),
            options: (Array.isArray(secretariesData?.data) ? secretariesData?.data ?? [] : []).map(
              (secretary: any) => ({ value: String(secretary.id), label: secretary.name })
            ),
            values: selectedSecretary ? [String(selectedSecretary)] : [],
            apply: (values: string[]) => setSelectedSecretary(values[0] ?? ''),
          },
          {
            key: 'institution',
            label: 'Instituição',
            icon: getFacetIcon('institution_id'),
            anyLabel: deriveAnyLabel('Instituição'),
            options: (Array.isArray(institutionsData?.data)
              ? institutionsData?.data ?? []
              : []
            ).map((institution: any) => ({
              value: String(institution.id),
              label: institution.name,
            })),
            values: selectedInstitution ? [String(selectedInstitution)] : [],
            apply: (values: string[]) => setSelectedInstitution(values[0] ?? ''),
          },
          {
            key: 'perPage',
            label: 'Itens por página',
            icon: getFacetIcon('type'),
            anyLabel: 'Padrão (20)',
            options: [
              { value: '20', label: '20 por página' },
              { value: '30', label: '30 por página' },
              { value: '50', label: '50 por página' },
            ],
            values: perPage === 20 ? [] : [String(perPage)],
            apply: (values: string[]) => handlePerPageChange(values[0] ?? '20'),
            searchable: false,
          },
        ]}
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          label: 'Buscar usuários',
          placeholder: 'Buscar por nome ou email',
        }}
        hasActiveFilters={Boolean(
          searchTerm || selectedGroup || selectedSecretary || selectedInstitution
        )}
        onClear={() => {
          setSearchTerm('');
          setSelectedGroup('');
          setSelectedSecretary('');
          setSelectedInstitution('');
        }}
        totalCount={pagination.total}
        countLabel="usuário"
        countLabelPlural="usuários"
      />

      <FramedCard>
        <FramedCardContent>
          {(loadingUsers || (!usersData && !usersError)) && users.length === 0 && (
            <DataSkeleton variant="table" rows={8} columns={5} label="Carregando usuários" />
          )}
          {!loadingUsers && usersError && users.length === 0 && <ErrorState onRetry={loadUsers} />}
          {!loadingUsers && !usersError && usersData && users.length === 0 && (
            <EmptyState
              variant="bare"
              title="Nenhum usuário encontrado"
              description="Altere os filtros para localizar outros usuários."
              action={{
                label: 'Limpar filtros',
                onClick: () => {
                  setSearchTerm('');
                  setSelectedGroup('');
                  setSelectedSecretary('');
                  setSelectedInstitution('');
                },
              }}
            />
          )}
          {users.length > 0 && (
            <>
              <TableContainer>
                <Table variant="serra">
                  <Thead>
                    <Tr bg="surface.frame">
                      <Th pl="16px">Nome</Th>
                      <Th>Grupo</Th>
                      <Th>Status</Th>
                      <Th pr="16px" textAlign="right">
                        Ações
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.map((user: User) => (
                      <Tr key={user.id}>
                        <Td pl="16px">
                          <RowIdentity title={user.name} meta={user.email} />
                        </Td>
                        <Td>
                          <Badge variant="neutral">{user.group?.name || 'Sem grupo'}</Badge>
                        </Td>
                        <Td>
                          <Badge variant={user.status ? 'success' : 'destructive'}>
                            {user.status ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </Td>
                        <Td pr="16px">
                          <Flex gap="2px" justify="flex-end">
                            <Tooltip label="Ver analytics" openDelay={300}>
                              <IconButton
                                aria-label="Ver analytics do usuário"
                                icon={<ChartLine size={16} />}
                                size="xs"
                                w="28px"
                                h="28px"
                                color="content.subtle"
                                variant="ghost"
                                _hover={{ bg: 'surface.selected', color: 'content.strong' }}
                                onClick={() => handleViewAnalytics(user)}
                              />
                            </Tooltip>
                            <Tooltip label="Editar permissões" openDelay={300}>
                              <IconButton
                                aria-label="Editar permissões"
                                icon={<Key size={16} />}
                                size="xs"
                                w="28px"
                                h="28px"
                                color="content.subtle"
                                variant="ghost"
                                _hover={{ bg: 'surface.selected', color: 'content.strong' }}
                                onClick={() => handleEditPermissions(user)}
                              />
                            </Tooltip>
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              <PaginationFooter
                totalItems={pagination.total}
                itemLabel="usuário"
                itemLabelPlural="usuários"
                currentPage={currentPage}
                totalPages={pagination.pages}
                onChange={setCurrentPage}
              />
            </>
          )}
        </FramedCardContent>
      </FramedCard>

      {/* Modal de Edição de Permissões */}
      <Modal autoFocus={false} isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Permissões - {selectedUser?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Como deseja configurar as permissões?</FormLabel>
                <RadioGroup value={permissionMode} onChange={(val: any) => setPermissionMode(val)}>
                  <Stack>
                    <Radio value="reuse">
                      <Text fontWeight="medium">Reaproveitar hierarquia existente</Text>
                      <Text fontSize="13px" color="content.body">
                        Aplicar um conjunto de permissões já configurado
                      </Text>
                    </Radio>
                    <Radio value="create">
                      <Text fontWeight="medium">Criar nova hierarquia personalizada</Text>
                      <Text fontSize="13px" color="content.body">
                        Definir permissões específicas para este usuário
                      </Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {permissionMode === 'reuse' && (
                <FormControl>
                  <FormLabel>Selecionar hierarquia</FormLabel>
                  <Select
                    placeholder="Escolha uma hierarquia..."
                    value={selectedHierarchy}
                    onChange={(e) => setSelectedHierarchy(e.target.value)}
                  >
                    {hierarchies.map((hierarchy) => (
                      <option key={hierarchy} value={hierarchy}>
                        {hierarchy}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {permissionMode === 'create' && (
                <>
                  <FormControl>
                    <FormLabel>Nome da nova hierarquia</FormLabel>
                    <Input
                      placeholder="Ex: Coordenadores, Supervisores..."
                      value={newHierarchyName}
                      onChange={(e) => setNewHierarchyName(e.target.value)}
                    />
                    {newHierarchyName && hierarchies.includes(newHierarchyName.trim()) && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        ⚠️ Já existe uma hierarquia com este nome
                      </Text>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Ações permitidas</FormLabel>
                    <CheckboxGroup
                      value={selectedActions}
                      onChange={(val: any) => setSelectedActions(val)}
                    >
                      <Stack spacing={2}>
                        <Checkbox value="create">
                          <Text fontWeight="medium">Criar (create)</Text>
                          <Text fontSize="13px" color="content.body">
                            Permite criar novos registros
                          </Text>
                        </Checkbox>
                        <Checkbox value="read">
                          <Text fontWeight="medium">Visualizar (read)</Text>
                          <Text fontSize="13px" color="content.body">
                            Permite visualizar registros
                          </Text>
                        </Checkbox>
                        <Checkbox value="update">
                          <Text fontWeight="medium">Editar (update)</Text>
                          <Text fontSize="13px" color="content.body">
                            Permite editar registros existentes
                          </Text>
                        </Checkbox>
                        <Checkbox value="delete">
                          <Text fontWeight="medium">Excluir (delete)</Text>
                          <Text fontSize="13px" color="content.body">
                            Permite excluir registros
                          </Text>
                        </Checkbox>
                      </Stack>
                    </CheckboxGroup>
                  </FormControl>
                </>
              )}
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={handleSavePermissions} isLoading={savingPermissions}>
              Salvar Permissões
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Analytics do Usuário */}
      <Modal autoFocus={false} isOpen={isAnalyticsOpen} onClose={onAnalyticsClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Analytics do Usuário - {selectedUser?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingAnalytics ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="md" color="content.subtle" />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Box p="16px" borderRadius="lg" borderWidth="1px" borderColor="line.hairline">
                  <Text fontSize="12px" color="content.faint" mb="8px">
                    Informações de Acesso
                  </Text>
                  <Stack spacing={3}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="13px" color="content.body">
                        Último login:
                      </Text>
                      <Text fontSize="13px" fontWeight={500} color="content.strong">
                        {formatDateTime(userAnalytics?.last_login || null)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="13px" color="content.body">
                        Última atividade:
                      </Text>
                      <Text fontSize="13px" fontWeight={500} color="content.strong">
                        {formatDateTime(userAnalytics?.last_activity || null)}
                      </Text>
                    </Flex>
                  </Stack>
                </Box>

                <Box p="16px" borderRadius="lg" borderWidth="1px" borderColor="line.hairline">
                  <Text fontSize="12px" color="content.faint" mb="8px">
                    Estatísticas de Uso
                  </Text>
                  <Stack spacing={3}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="13px" color="content.body">
                        Total de logins:
                      </Text>
                      <Badge variant="neutral">{userAnalytics?.login_count || 0}</Badge>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="13px" color="content.body">
                        Duração média da sessão:
                      </Text>
                      <Text fontSize="13px" fontWeight={500} color="content.strong">
                        {formatDuration(userAnalytics?.session_duration || null)}
                      </Text>
                    </Flex>
                  </Stack>
                </Box>

                <Box p="16px" borderRadius="lg" borderWidth="1px" borderColor="line.hairline">
                  <Text fontSize="12px" color="content.faint" mb="8px">
                    Informações do Usuário
                  </Text>
                  <Stack spacing={3}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="13px" color="content.body">
                        Email:
                      </Text>
                      <Text fontSize="13px" fontWeight={500} color="content.strong">
                        {selectedUser?.email}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="13px" color="content.body">
                        Grupo:
                      </Text>
                      <Badge variant="neutral">{selectedUser?.group?.name || 'Sem grupo'}</Badge>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="13px" color="content.body">
                        Status:
                      </Text>
                      <Badge variant={selectedUser?.status ? 'success' : 'destructive'}>
                        {selectedUser?.status ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Flex>
                  </Stack>
                </Box>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onAnalyticsClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserAccessManagementPage;
